// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import constants from "@store/constants";
import getCurrentDateMinuteZero from "@store/Framework/Common/Utilities/getCurrentDateMinuteZero";
import getEventList, {
    GetEventsApiParameter,
} from "@store/Framework/Store/Store/Pages/EventList/Handlers/GetEventList";
import EventListTile, { EventListTileStyles } from "@store/Library/Components/Events/EventListTile";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { carouselProps } from "@store/Themes/common/props/carousel";
import breakpointValues, { BreakpointValue } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { usePageLinkByNodeId } from "@toolkit/Framework/Store/Links/LinksHooks";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    linkText = "linkText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.linkText]: string;
    };
}

type Props = OwnProps;

export interface EventDetailsUpcomingCarouselStyles {
    loadingOverlay?: LoadingOverlayProps;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    eventListLink?: LinkPresentationProps;
    tileStyle?: EventListTileStyles;
    wrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideWrapperStyles?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    dotButtonSelected?: ButtonPresentationProps;
}

const verticalPadding: BreakpointValue[] = pxToRemArray([0, null, 30]);

const eventDetailsUpcomingCarouselStyles: EventDetailsUpcomingCarouselStyles = {
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;

            ${breakpointValues(pxToRemArray([0, null, 15]), "margin-bottom")}
        `,
    },
    title: {
        variant: "h3",
        forwardAs: "h2",
        css: css`
            margin: ${pxToRem(5)} 0;
        `,
    },
    eventListLink: {
        css: css`
            ${linkStyles("primary.main", "primary.dark", true, false)}
            margin: ${pxToRem(5)} 0;
            font-weight: ${x => x.theme.design.typography.weight.semibold};
        `,
    },
    tileStyle: {
        wrapper: {
            css: css`
                ${breakpointValues(verticalPadding, ["padding-top", "padding-bottom"])}
                ${breakpointValues([0, null, "1px"], ["border-width"])}
            `,
        },
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideWrapperStyles: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;

            ${({ theme }) => css`
                ${breakpointValues(theme.design.layout.spacing.content, ["padding"])}
            `}
        `,
    },
    prevArrowWrapper: carouselProps.prevArrowWrapper,
    prevArrowButton: carouselProps.prevArrowButton,
    dotsContainerWrapper: carouselProps.dotsWrapper,
    dotButton: carouselProps.dotButton,
    dotButtonSelected: carouselProps.dotButtonSelected,
    nextArrowWrapper: carouselProps.nextArrowWrapper,
    nextArrowButton: carouselProps.nextArrowButton,
};

const styles = eventDetailsUpcomingCarouselStyles;

const EventDetailsUpcomingCarousel: FC<Props> = ({ fields }) => {
    const minFromDate = getCurrentDateMinuteZero();
    const initialParameter: GetEventsApiParameter = {
        startDate: convertDateToDateOnlyString(minFromDate),
        pageSize: 4,
    };
    const apiParameter: GetEventsApiParameter = { ...initialParameter };
    const dispatch = useDispatch();

    const eventListDataView = useAppSelector(state => getDataView(state.data.pages, apiParameter));
    const { isLoading, value: eventList } = eventListDataView;
    const currentPageNodeId = useCurrentPage().nodeId;
    const parentUrl = usePageLinkByNodeId(useCurrentPage().parentId)?.url;
    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!isLoading || !eventList) {
            dispatch(getEventList(apiParameter));
        }
    }, []);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    useEffect(() => {
        if (!embla || !eventList || eventList.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, eventList]);

    const eventListItems = eventList?.filter(item => item.nodeId !== currentPageNodeId).slice(0, 3);

    if (!eventList || !eventListItems?.length) {
        return null;
    }

    const renderTile = (page: PageProps) => {
        const {
            fields: {
                urlSegment,
                startDate,
                endDate,
                hideHourMinutes,
                dateTimezone,
                isLocalBranch,
                localBranchTitle,
                addressWrapper,
                previewImage,
                categoriesList,
                eventSummary,
                linkLabel,
            },
        } = page;

        return (
            <EventListTile
                key={page.id}
                title={page.fields["title"]}
                titleForwardAs="h3"
                eventLink={`${parentUrl}/${urlSegment}`}
                startDate={startDate}
                endDate={endDate}
                hideHourMinutes={hideHourMinutes}
                dateTimezone={dateTimezone}
                isLocalBranch={isLocalBranch}
                localBranchTitle={localBranchTitle}
                addressWrapper={addressWrapper}
                previewImage={previewImage}
                categoriesList={categoriesList}
                eventSummary={eventSummary}
                linkLabel={linkLabel}
                extendedStyles={styles.tileStyle}
            />
        );
    };

    return (
        <DataViewLoadingOverlay dataView={eventListDataView} loading={isLoading} {...styles.loadingOverlay}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title}>{fields.title}</Typography>
                <Link href={parentUrl} {...styles.eventListLink}>
                    {fields.linkText}
                </Link>
            </StyledWrapper>
            <Hidden above="sm" {...styles.wrapper}>
                {eventListItems.length > 1 && (
                    <StyledWrapper {...styles.prevArrowWrapper}>
                        <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                            <VisuallyHidden>{translate("Previous slide")}</VisuallyHidden>
                        </Button>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper {...styles.slideContainerWrapper}>
                        {eventListItems.map((page: PageProps) => (
                            <StyledWrapper key={`slide_${page.id}`} {...styles.slideWrapperStyles}>
                                {renderTile(page)}
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                    {eventListItems.map((_, i) => {
                        return (
                            <Button
                                className="slide-dot-button"
                                key={`slide-dot-${_.id}`}
                                {...(i === selectedIndex ? styles.dotButtonSelected : styles.dotButton)}
                                onClick={() => embla && embla.scrollTo(i)}
                            />
                        );
                    })}
                </StyledWrapper>
                {eventListItems.length > 1 && (
                    <StyledWrapper className="slide-arrow-wrapper" {...styles.nextArrowWrapper}>
                        <Button
                            className="slide-arrow"
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                        >
                            <VisuallyHidden>{translate("Next slide")}</VisuallyHidden>
                        </Button>
                    </StyledWrapper>
                )}
            </Hidden>
            <Hidden below="md" {...styles.wrapper}>
                {eventListItems.map((page: PageProps) => {
                    return renderTile(page);
                })}
            </Hidden>
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: EventDetailsUpcomingCarousel,
    definition: {
        group: constants.widgetGroup.eventDetails,
        allowedContexts: ["EventDetailsPage"] as any,
        displayName: "Upcoming Events",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Upcoming Events Title",
                defaultValue: "Upcoming Events",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.linkText,
                displayName: "Upcoming Events Link",
                defaultValue: "View All Upcoming Events",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
